import React from 'react';
import {
  withSystem,
  Plugins,
  Atom,
  AtomRenderProps,
  Expressions,
} from '@packages/systems/wfdl';
import {
  iconName,
  typeName,
  qualify,
  pluginName,
  namesEqual,
} from '@packages/systems/wfdl-Qualified';
import {weakMemo} from '@packages/utilities/memo';
import {
  TTypeConstructor,
  TBoolean,
  TText,
  ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {createDevlinkAtom} from '@packages/utilities/integrated-types';
import {transformers} from '@packages/systems/dynamo/utils/Transformers';
import {defaultRichTextChildrenNodePojos} from '@packages/systems/core/constants/DomNodeDefaults';
import {
  RenderContext,
  useCollectionIdToPageIdMap,
  useSite,
} from '@packages/systems/render';
import {
  EvaluatedNodeData,
  getChildren,
  getInPojo,
  getTagOrElse,
} from '@packages/utilities/data-utils';
import {useEnclosingRichText, withEnclosingRichText} from './enclosingRichText';
const basicPlugin = Plugins.name('Basic');

type HrefMap = AtomRenderProps['sharedContext']['hrefMap'];
type PageLinkHrefPrefix = RenderContext['site']['pageLinkHrefPrefix'];
type CollectionIdToPageIdMap = RenderContext['collectionIdToPageIdMap'];

// TODO: (javi, david) Decide on which way this atom will be getting its children.
// If we use `rteHtmlToJsonNodes` then we don't need to update the links
// as they would be updated by each atom inside the rich text, but we should
// then update the Link atom to also read from `data-rt-link-pageid`.

// TEMP (yoav): dyn-bound rich text gets an html string from the binding as
// content that's setDangerously, static rich text gets a subtree of
// elements. Long-term we'll be migrating dynamic rich text fields to be stored
// as jsonNodes and tag enforced, currently dynamic rich text imported via
// zapier/webflow api may not yet be tag enforced and we don't want to break
// production sites, so if we get an html string in the mean time we should just
// set it dangerously until we can migrate all that data

// This is so we can detect whether we are in a rich text atom from children atoms
// @ts-expect-error - TS7006 - Parameter 'context' implicitly has an 'any' type.
const updateContext = weakMemo((context) =>
  context.set('insideRichText', true)
);

const RichText = Atom.createRender(
  ({
    data,
    context,
    attributes,
    classes,
    generateAttributes,
    renderChild,
    innerHTML,
    sharedContext: {hrefMap},
    nativeIdPath,
    environmentContext,
  }) => {
    const Tag = getTagOrElse(getInPojo(data, ['tag']), 'div');
    const slot = getInPojo(data, ['slot']) ?? null;
    const children = getChildren(data);
    const rteClasses = classes.push('w-richtext');
    const collectionIdToPageIdMap = useCollectionIdToPageIdMap();
    const {pageLinkHrefPrefix} = useSite();
    const enclosingRichText = useEnclosingRichText(
      children,
      nativeIdPath,
      environmentContext
    );

    const legacyDynamoRichTextHtml = getLegacyDynamoRichTextHtmlBindingValue({
      children,
      innerHTML,
      pageLinkHrefPrefix,
      hrefMap,
      collectionIdToPageIdMap,
    });

    // When legacyDynamoRichTextHtml is a string, it means that the CMS still has the Rich Text value stored
    // as a raw HTML string (and not as a JSON nodes that is the modern way). When the value is an HTML string,
    // we need to render the value with dangerouslySetInnerHTML to keep the renderer output backward compatible.
    // When the user updates the Rich Text in the CMS, we start storing the values as JSON nodes.
    const renderDangerously = typeof legacyDynamoRichTextHtml === 'string';

    const dynamicInnerHtmlProps = renderDangerously
      ? {
          dangerouslySetInnerHTML: {
            __html: legacyDynamoRichTextHtml,
          },
        }
      : {};

    return (
      <Tag
        {...generateAttributes(attributes, rteClasses)}
        {...dynamicInnerHtmlProps}
        {...(slot ? {slot} : {})}
      >
        {renderDangerously
          ? null
          : children.map((child: EvaluatedNodeData) =>
              renderChild(
                child,
                withEnclosingRichText(updateContext(context))(enclosingRichText)
              )
            )}
      </Tag>
    );
  }
);

const config = createDevlinkAtom({
  displayName: 'Rich Text Block',
  icon: {type: 'Component', value: [basicPlugin, iconName('RichText')]},
  showEmpty: true,
  render: RichText,
  fields: {
    tag: withSystem(
      {
        label: 'Tag',
        private: true,
        defaultValue: 'div',
        isLocalizable: true,
      },
      TTypeConstructor(qualify(pluginName('Basic'), typeName('TagsDropdown')))
    ),
    rich: withSystem(
      {
        private: true,
      },
      TBoolean
    ),
    children: withSystem(
      {
        label: 'Content',
        isBindable: true,
        isLocalizable: true,
        defaultValue: defaultRichTextChildrenNodePojos,
      },
      TTypeConstructor(qualify(basicPlugin, typeName('RichTextChildren')))
    ),
    slot: withSystem(
      {label: 'Slot', isBindable: false, private: true, defaultValue: ''},
      TText
    ),
  },
});

export default config;

function getLegacyDynamoRichTextHtmlBindingValue({
  children,
  innerHTML,
  pageLinkHrefPrefix,
  hrefMap,
  collectionIdToPageIdMap,
}: {
  children: unknown[];
  innerHTML: AtomRenderProps['innerHTML'];
  pageLinkHrefPrefix: PageLinkHrefPrefix;
  hrefMap: HrefMap;
  collectionIdToPageIdMap: CollectionIdToPageIdMap;
}): string | null {
  if (typeof innerHTML === 'string') {
    // The `innerHTML` legacy Dynamo binding prop has already gone through the `rich` transformer.
    return innerHTML;
  }

  if (children.length === 1 && children[0]) {
    const child = children[0];
    if (
      React.isValidElement(child) &&
      child.props &&
      typeof child.props === 'object' &&
      'element' in child.props
    ) {
      const childElement = child.props.element as ExpressionElement;
      if (isDynamoInterimLegacyRichTextValue(childElement)) {
        const value = Expressions.getElementDataAt(
          childElement,
          [{in: 'Record', at: 'value'}],
          'Text',
          null
        );

        if (value) {
          const collectionSlugMap: {[pageId: string]: string} = {};
          for (const collectionId in collectionIdToPageIdMap) {
            const collectionTemplatePageId =
              collectionIdToPageIdMap[collectionId];
            // @ts-expect-error - TS2538 - Type 'undefined' cannot be used as an index type.
            const pathToPage = hrefMap[collectionTemplatePageId];
            const pathToCollectionTemplatePage =
              typeof pathToPage === 'string'
                ? pathToPage.replace(/^\//, '')
                : null;

            if (pathToCollectionTemplatePage) {
              collectionSlugMap[collectionId] = pathToCollectionTemplatePage;
            }
          }

          return transformers(
            value,
            {type: 'rich', params: []},
            {
              pageLinkHrefPrefix,
              collectionSlugMap,
            }
          );
        }
      }
    }
  }

  return null;
}

const dynamoInterimLegacyRichTextValueType = qualify(
  'Dynamo',
  'InterimLegacyRichTextValue'
);

const isDynamoInterimLegacyRichTextValue = (element: ExpressionElement) => {
  return namesEqual(element.type, dynamoInterimLegacyRichTextValueType);
};
